import { useCallback, useEffect, useState } from "react";

import AsyncCombobox, { type AsyncOption } from "@/components/async-combobox";
import { Label } from "@/components/ui/label";

interface CategorySubcategorySelectorProps {
  categoryId?: number | string | null;
  subcategoryId?: number | string | null;
  onCategoryChange: (id: number | null, option: AsyncOption | null) => void;
  onSubcategoryChange: (id: number | null, option: AsyncOption | null) => void;
  categoryError?: string;
  subcategoryError?: string;
  disabled?: boolean;
}

export function CategorySubcategorySelector({
  categoryId,
  subcategoryId,
  onCategoryChange,
  onSubcategoryChange,
  categoryError,
  subcategoryError,
  disabled = false,
}: CategorySubcategorySelectorProps) {
  const [categoryOption, setCategoryOption] = useState<AsyncOption | null>(
    null,
  );
  const [subcategoryOption, setSubcategoryOption] =
    useState<AsyncOption | null>(null);

  // Fetch initial category label if categoryId is provided but no option is set
  useEffect(() => {
    if (
      categoryId &&
      (!categoryOption || String(categoryOption.value) !== String(categoryId))
    ) {
      const fetchCategory = async () => {
        try {
          const res = await fetch(`/categories/search?ids[]=${categoryId}`);
          if (res.ok) {
            const data = (await res.json()) as AsyncOption[];
            const match = data.find(
              (opt) => String(opt.value) === String(categoryId),
            );
            if (match) setCategoryOption(match);
          }
        } catch (e) {
          console.error("Error fetching category label", e);
        }
      };
      void fetchCategory();
    } else if (!categoryId) {
      setCategoryOption(null);
    }
  }, [categoryId]);

  // Fetch initial subcategory label if subcategoryId is provided
  useEffect(() => {
    if (
      categoryId &&
      subcategoryId &&
      (!subcategoryOption ||
        String(subcategoryOption.value) !== String(subcategoryId))
    ) {
      const fetchSubcategory = async () => {
        try {
          const res = await fetch(
            `/api/categories/${categoryId}?ids[]=${subcategoryId}`,
          );
          if (res.ok) {
            const data = (await res.json()) as AsyncOption[];
            const match = data.find(
              (opt) => String(opt.value) === String(subcategoryId),
            );
            if (match) setSubcategoryOption(match);
          }
        } catch (e) {
          console.error("Error fetching subcategory label", e);
        }
      };
      void fetchSubcategory();
    } else if (!subcategoryId) {
      setSubcategoryOption(null);
    }
  }, [categoryId, subcategoryId]);

  const loadCategoryOptions = useCallback(
    async (inputValue: string): Promise<AsyncOption[]> => {
      const params = new URLSearchParams();
      if (inputValue) params.set("term", inputValue);
      if (categoryId) params.append("ids[]", String(categoryId));

      try {
        const res = await fetch(`/categories/search?${params.toString()}`);
        if (!res.ok) return [];
        return (await res.json()) as AsyncOption[];
      } catch {
        return [];
      }
    },
    [categoryId],
  );

  const loadSubcategoryOptions = useCallback(
    async (inputValue: string): Promise<AsyncOption[]> => {
      if (!categoryId) return [];

      const params = new URLSearchParams();
      if (inputValue) params.set("term", inputValue);
      if (subcategoryId) params.append("ids[]", String(subcategoryId));

      try {
        const res = await fetch(
          `/api/categories/${categoryId}?${params.toString()}`,
        );
        if (!res.ok) return [];
        return (await res.json()) as AsyncOption[];
      } catch {
        return [];
      }
    },
    [categoryId, subcategoryId],
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-2">
        <Label>Categoría *</Label>
        <AsyncCombobox
          value={categoryOption}
          onChange={(opt) => {
            setCategoryOption(opt);
            onCategoryChange(opt ? Number(opt.value) : null, opt);
            // Auto-clear subcategory when category changes
            setSubcategoryOption(null);
            onSubcategoryChange(null, null);
          }}
          loadOptions={loadCategoryOptions}
          placeholder="Seleccione una categoría"
          disabled={disabled}
        />
        {categoryError && (
          <p className="text-sm text-red-500">{categoryError}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Subcategoría</Label>
        <AsyncCombobox
          key={String(categoryId ?? "none")} // Re-mount when category changes to clear internal state
          value={subcategoryOption}
          onChange={(opt) => {
            setSubcategoryOption(opt);
            onSubcategoryChange(opt ? Number(opt.value) : null, opt);
          }}
          loadOptions={loadSubcategoryOptions}
          placeholder={
            categoryId
              ? "Seleccione una subcategoría"
              : "Primero seleccione una categoría"
          }
          disabled={disabled || !categoryId}
        />
        {subcategoryError && (
          <p className="text-sm text-red-500">{subcategoryError}</p>
        )}
      </div>
    </div>
  );
}
